import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { api } from '../../../app/auth/apiSlice';
import { NoticeProp } from '../../../components/Admin/Notice/store/noticeSlice';

interface BannerResponse {
  id?: number;
  image_en: string;
  image_bn: string;
  alt_text_en: string;
  alt_text_bn: string;
  video: string;
  link: string;
}

interface SecondaryLogoResponse {
  dark_mode_image: string;
  light_mode_image: string;
  alt_text: string;
}

export interface SliderProp {
  id: number | string | undefined;
  image: string;
  alt_text: string;
  is_active: boolean;
  is_popup: boolean;
  order: number;
}

interface EventsResponse {
  image: string;
  link: string;
  title_bn: string;
  title_en: string;
}

interface DashboardImageResponse {
  banner: BannerResponse;
  dashboard_sliders: SliderProp[];
  events: EventsResponse[];
  secondary_logo: SecondaryLogoResponse;
}

interface ImportantDocumentsResponse {
  results: [
    {
      title: string;
      image: string;
      link: string;
    }
  ];
}

interface FactsResponse {
  fact_en: string;
  fact_bn: string;
}

export interface TaxResponse {
  MaxallowableInv: number;
  TxYear: number;
}

export type TaxResponseArray = TaxResponse[];

export interface VisitProp {
  id: number | string | undefined;
  icon_name: string | null;
  title_en: string | null;
  title_bn: string | null;
  link: string | null;
  tag: string;
  is_active: boolean;
}
export interface NewsItem {
  id: number;
  content_en: string;
  content_bn: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  sequence: number;
}

interface FactCardResponse {
  icon_name: string;
  title_en: string;
  title_bn: string;
}

interface WeatherResponse {
  temp: string;
  description: string;
  icon: string;
}
interface CityProp {
  properties: {
    city: string;
  };
}
interface LocationResponse {
  features: CityProp[];
}
interface GmailUnreadResponse {
  id: string;
  messagesTotal: number;
  messagesUnread: number;
  name: string;
  threadsTotal: number;
  threadsUnread: number;
  type: number;
}

interface DashboardContentResponse {
  external_cards: VisitProp[];
  facts: FactsResponse[];
  factcard: FactCardResponse;
  notices: NewsItem[];
  header_notices: NoticeProp;
}

const DashboardApi = api
  .enhanceEndpoints({
    addTagTypes: [
      'BannerImage',
      'Weather',
      'DashboardImage',
      'Tax',
      'ImportantDocuments',
      'GoogleIntegration',
      'DashboardContent',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      postBannerVideoData: builder.mutation({
        query: (data) => {
          return {
            url: `v1/web-contents/banner-details/`,
            method: 'POST',
            body: data,
          };
        },
        invalidatesTags: ['BannerImage'],
      }),
      getTax: builder.query<TaxResponseArray, void>({
        query: () => {
          return `v1/integrations/bracapi/tax-rebate-investment`;
        },
        providesTags: ['Tax'],
      }),
      getImportantDocuments: builder.query<ImportantDocumentsResponse, void>({
        query: () => {
          return `v1/web-contents/public/important-links/`;
        },
        providesTags: ['ImportantDocuments'],
      }),
      getWeatherUpdate: builder.query<
        WeatherResponse,
        { lat: string; lon: string }
      >({
        query: ({ lat, lon }) => {
          return `v1/web-contents/weather/dashboard/?lat=${lat}&lon=${lon}`;
        },
        providesTags: ['Weather'],
      }),
      googleApiIntegration: builder.mutation({
        query: (data) => {
          return {
            url: `v1/main/google-connect/token`,
            method: 'POST',
            body: data,
          };
        },
        invalidatesTags: ['GoogleIntegration'],
      }),
      dashBoardImages: builder.query<DashboardImageResponse, void>({
        query: () => {
          return `v1/web-contents/dashboard-images/`;
        },
        providesTags: ['DashboardImage'],
      }),
      dashBoardContent: builder.query<DashboardContentResponse, void>({
        query: () => {
          return `v1/web-contents/dashboard-contents/`;
        },
        providesTags: ['DashboardContent'],
      }),
    }),
  });

const locationAPIKey = import.meta.env.VITE_LOCATION_API_KEY;
const locationAPI = import.meta.env.VITE_LOCATION_API;

export const locationApi = createApi({
  reducerPath: 'locationApi',
  baseQuery: fetchBaseQuery({ baseUrl: locationAPI }),
  endpoints: (builder) => ({
    getLocationUpdate: builder.query<
      LocationResponse,
      { lat: string; lon: string }
    >({
      query: ({ lat, lon }) => {
        return `/geocode/reverse?lat=${lat}&lon=${lon}&apiKey=${locationAPIKey}`;
      },
    }),
  }),
});

const gamilAPI = import.meta.env.VITE_GOOGLE_API;

export const gamilApi = createApi({
  reducerPath: 'gamilApi',
  baseQuery: fetchBaseQuery({ baseUrl: gamilAPI }),
  endpoints: (builder) => ({
    gmailUnreadMailCount: builder.query<
      GmailUnreadResponse,
      string | undefined
    >({
      query: (accessToken) => {
        return `users/me/labels/UNREAD?access_token=${accessToken}`;
      },
    }),
  }),
});

export const {
  useGetWeatherUpdateQuery,
  useGetImportantDocumentsQuery,
  useGetTaxQuery,
  usePostBannerVideoDataMutation,
  useGoogleApiIntegrationMutation,
  useDashBoardImagesQuery,
  useDashBoardContentQuery,
} = DashboardApi;

export const { useGetLocationUpdateQuery } = locationApi;
export const { useGmailUnreadMailCountQuery } = gamilApi;
